import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

interface ApiCredential {
  id: string;
  category: string;
  key: string;
  value: string | null;
  isSecret: boolean;
  description: string | null;
  isConfigured: boolean;
  updatedAt: string;
}

interface ApiService {
  id: string;
  name: string;
  description: string;
  docsUrl: string;
  credentials: { key: string; label: string; placeholder: string }[];
  free: boolean;
}

const API_SERVICES: ApiService[] = [
  {
    id: 'sentinel_hub',
    name: 'Sentinel Hub (Copernicus)',
    description: 'Free Sentinel-2 satellite imagery with 10m resolution, 5-day revisit',
    docsUrl: 'https://dataspace.copernicus.eu/',
    free: true,
    credentials: [
      {
        key: 'sentinel_hub_client_id',
        label: 'Client ID',
        placeholder: 'e.g., sh-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      },
      {
        key: 'sentinel_hub_client_secret',
        label: 'Client Secret',
        placeholder: 'Your OAuth client secret',
      },
    ],
  },
  {
    id: 'planet',
    name: 'Planet Labs',
    description: 'High-resolution 3m daily imagery (PlanetScope), 0.5m SkySat',
    docsUrl: 'https://www.planet.com/explorer/',
    free: false,
    credentials: [
      {
        key: 'planet_api_key',
        label: 'API Key',
        placeholder: 'Your Planet Labs API key',
      },
    ],
  },
  {
    id: 'landsat',
    name: 'Landsat (USGS)',
    description: 'Free 30m resolution imagery, historical archive since 1984',
    docsUrl: 'https://earthexplorer.usgs.gov/',
    free: true,
    credentials: [
      {
        key: 'landsat_api_key',
        label: 'API Key',
        placeholder: 'Your USGS API key',
      },
    ],
  },
  {
    id: 'copernicus',
    name: 'Copernicus Open Access Hub',
    description: 'Direct access to Sentinel data (alternative to Sentinel Hub)',
    docsUrl: 'https://scihub.copernicus.eu/',
    free: true,
    credentials: [
      {
        key: 'copernicus_user',
        label: 'Username',
        placeholder: 'Your Copernicus username',
      },
      {
        key: 'copernicus_password',
        label: 'Password',
        placeholder: 'Your Copernicus password',
      },
    ],
  },
  {
    id: 'openweather',
    name: 'OpenWeatherMap',
    description: 'Weather forecasts and historical data (alternative to Open-Meteo)',
    docsUrl: 'https://openweathermap.org/api',
    free: true,
    credentials: [
      {
        key: 'openweather_api_key',
        label: 'API Key',
        placeholder: 'Your OpenWeatherMap API key',
      },
    ],
  },
];

export default function ApiConfig() {
  const [credentials, setCredentials] = useState<ApiCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/v1/admin/api-credentials`, { headers });
      setCredentials(response.data.credentials);
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
      toast.error('Failed to load API credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key: string) => {
    try {
      await axios.put(
        `${baseUrl}/api/v1/admin/api-credentials/${key}`,
        { value: editValue },
        { headers }
      );
      toast.success('Credential saved successfully');
      setEditingKey(null);
      setEditValue('');
      fetchCredentials();
    } catch (error) {
      console.error('Failed to save credential:', error);
      toast.error('Failed to save credential');
    }
  };

  const handleTest = async (key: string) => {
    setTesting(key);
    try {
      const response = await axios.post(
        `${baseUrl}/api/v1/admin/api-credentials/${key}/test`,
        {},
        { headers }
      );
      setTestResults((prev) => ({ ...prev, [key]: response.data }));
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Test failed';
      setTestResults((prev) => ({ ...prev, [key]: { success: false, message } }));
      toast.error(message);
    } finally {
      setTesting(null);
    }
  };

  const getCredentialStatus = (key: string): boolean => {
    const cred = credentials.find((c) => c.key === key);
    return cred?.isConfigured || false;
  };

  const getCredentialValue = (key: string): string => {
    const cred = credentials.find((c) => c.key === key);
    return cred?.value || '';
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">API Configuration</h1>
        <p className="text-gray-500 mt-1">
          Configure external API credentials for satellite data and weather services
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <QuestionMarkCircleIcon className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Getting Started</p>
            <p className="mt-1">
              We recommend starting with <strong>Sentinel Hub</strong> (free) for satellite imagery.
              Register at{' '}
              <a
                href="https://dataspace.copernicus.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Copernicus Data Space
              </a>
              , then create an OAuth client in Dashboard → Account Settings.
            </p>
          </div>
        </div>
      </div>

      {/* API Services */}
      <div className="space-y-6">
        {API_SERVICES.map((service) => {
          const allConfigured = service.credentials.every((c) => getCredentialStatus(c.key));

          return (
            <div key={service.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Service Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-lg mr-4 ${
                      allConfigured ? 'bg-green-100' : 'bg-gray-100'
                    }`}
                  >
                    <KeyIcon
                      className={`h-6 w-6 ${allConfigured ? 'text-green-600' : 'text-gray-400'}`}
                    />
                  </div>
                  <div>
                    <div className="flex items-center">
                      <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                      <span
                        className={`ml-3 text-xs px-2 py-0.5 rounded-full ${
                          service.free
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {service.free ? 'Free' : 'Paid'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{service.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {allConfigured ? (
                    <span className="flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      Configured
                    </span>
                  ) : (
                    <span className="flex items-center text-sm text-gray-400">
                      <XCircleIcon className="h-5 w-5 mr-1" />
                      Not configured
                    </span>
                  )}
                  <a
                    href={service.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Get credentials →
                  </a>
                </div>
              </div>

              {/* Credentials Form */}
              <div className="px-6 py-4 space-y-4">
                {service.credentials.map((cred) => {
                  const isEditing = editingKey === cred.key;
                  const isConfigured = getCredentialStatus(cred.key);
                  const currentValue = getCredentialValue(cred.key);
                  const testResult = testResults[cred.key];

                  return (
                    <div key={cred.key} className="flex items-start space-x-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {cred.label}
                        </label>
                        {isEditing ? (
                          <div className="flex items-center space-x-2">
                            <div className="relative flex-1">
                              <input
                                type={showPassword[cred.key] ? 'text' : 'password'}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                placeholder={cred.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowPassword((prev) => ({
                                    ...prev,
                                    [cred.key]: !prev[cred.key],
                                  }))
                                }
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showPassword[cred.key] ? (
                                  <EyeSlashIcon className="h-5 w-5" />
                                ) : (
                                  <EyeIcon className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            <button
                              onClick={() => handleSave(cred.key)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingKey(null);
                                setEditValue('');
                              }}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 font-mono text-sm">
                              {isConfigured ? currentValue : 'Not configured'}
                            </div>
                            <button
                              onClick={() => {
                                setEditingKey(cred.key);
                                setEditValue('');
                              }}
                              className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-700 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                            >
                              {isConfigured ? 'Update' : 'Configure'}
                            </button>
                            {isConfigured && (
                              <button
                                onClick={() => handleTest(cred.key)}
                                disabled={testing === cred.key}
                                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                              >
                                {testing === cred.key ? (
                                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Test'
                                )}
                              </button>
                            )}
                          </div>
                        )}
                        {testResult && (
                          <p
                            className={`mt-1 text-sm ${
                              testResult.success ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {testResult.message}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
        <strong>Security Note:</strong> All credentials are encrypted with AES-256-GCM before being
        stored in the database. Only administrators can view or modify these settings.
      </div>
    </div>
  );
}
